// Copyright 2026 TokenLat. Licensed under the Apache License, Version 2.0.
export const recoveryKey = 'tl-coze:auth-recovery';
export const logoutKey = 'tl-coze:logged-out';

export const markLoggedOut = () => {
  window.dispatchEvent(
    new CustomEvent('tl-coze:account-changed', { detail: { loggedOut: true } }),
  );
  try {
    localStorage.setItem('tl-coze:account-changed', `logout:${Date.now()}`);
  } catch {
    // Focus rechecks permissions if cross-tab storage is unavailable.
    window.dispatchEvent(
      new CustomEvent('tl-coze:account-changed', {
        detail: { loggedOut: true },
      }),
    );
  }
  try {
    sessionStorage.setItem(logoutKey, 'true');
    return true;
  } catch {
    // The logout redirect also carries a marker when storage is unavailable.
    return false;
  }
};

export class AuthenticationError extends Error {
  constructor(public readonly status: number) {
    super(`Authentication request failed (${status})`);
  }
}

let configuration: Promise<boolean> | undefined;
export const tokenLatEnabled = () => {
  configuration ??= fetch('/api/auth/config', {
    credentials: 'same-origin',
    cache: 'no-store',
  })
    .then(async response => {
      if (!response.ok) {
        throw new AuthenticationError(response.status);
      }
      const config: { enabled: boolean } = await response.json();
      if (typeof config.enabled !== 'boolean') {
        throw new AuthenticationError(503);
      }
      return config.enabled;
    })
    .catch((error: unknown) => {
      configuration = undefined;
      throw error;
    });
  return configuration;
};

export const safeReturnPath = (raw: string | null) => {
  if (
    !raw ||
    !raw.startsWith('/') ||
    raw.startsWith('//') ||
    /[\\\r\n]/.test(raw)
  ) {
    return '/space';
  }
  try {
    const target = new URL(raw, window.location.origin);
    if (
      target.origin !== window.location.origin ||
      /^\/(auth|sign|api)(\/|$)/.test(target.pathname)
    ) {
      return '/space';
    }
    return `${target.pathname}${target.search}${target.hash}`;
  } catch {
    return '/space';
  }
};

export const beginAuthorization = (
  next: string,
  intent: 'login' | 'register',
) => {
  const params = new URLSearchParams({
    next: safeReturnPath(next),
    intent,
    locale: navigator.language.startsWith('zh') ? 'zh-CN' : 'en-US',
  });
  window.location.assign(`/api/auth/login?${params}`);
};

export const allowAuthorizationRecovery = () => {
  try {
    if (
      sessionStorage.getItem(recoveryKey) ||
      sessionStorage.getItem(logoutKey)
    ) {
      return false;
    }
    sessionStorage.setItem(recoveryKey, 'attempted');
    return true;
  } catch {
    // Without browser storage, recovery cannot reliably stop a redirect loop.
    return false;
  }
};

export const finishAuthorization = () => {
  window.dispatchEvent(
    new CustomEvent('tl-coze:account-changed', {
      detail: { loggedOut: false },
    }),
  );
  try {
    localStorage.setItem('tl-coze:account-changed', `login:${Date.now()}`);
  } catch {
    // Keep the current tab usable when cross-tab storage is unavailable.
    window.dispatchEvent(
      new CustomEvent('tl-coze:account-changed', {
        detail: { loggedOut: false },
      }),
    );
  }
  try {
    sessionStorage.removeItem(recoveryKey);
    sessionStorage.removeItem(logoutKey);
  } catch {
    // Manual authorization remains usable when storage is unavailable.
    return;
  }
};
